import React from 'react';
import { Card, Spinner, Alert, Button, Row, Col } from 'react-bootstrap';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateTime, formatTime, formatFieldValue as formatFieldValueUtil } from '../../utils/formatUtils';
import { theme } from '../../theme/theme.js';
import { useSearchViewWidgetData, extractPrimitiveValue } from './hooks/useSearchViewWidgetData';

/**
 * SearchWidget Component
 * Displays search results based on a search view configuration
 * Supports field bindings from the detail record to search parameters
 */
const SearchWidget = ({
  widget,
  currentRecordId,
  sourceObjectName,
  recordData
}) => {
  const navigate = useNavigate();

  const {
    searchViewConfig,
    records,
    loading,
    error,
    fieldDataTypes,
    fieldMetadata,
    searchObjectName,
    widgetConfig,
    selectedFields,
    handleViewAll
  } = useSearchViewWidgetData(widget, recordData);

  const searchViewId = widgetConfig?.search_view_id;
  const maxHeight = widgetConfig.max_height || 400;

  const formatFieldValue = (rawValue, fieldName) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '—';
    }

    const metadata = fieldMetadata[fieldName] || {};
    const dataType = (metadata.data_type || fieldDataTypes[fieldName] || 'text').toLowerCase();
    const displayValue =
      typeof rawValue === 'object' && rawValue !== null
        ? extractPrimitiveValue(rawValue.displayValue)
        : null;
    const primitiveValue = extractPrimitiveValue(rawValue);

    if (['lookup', 'reference'].includes(dataType)) {
      return displayValue || primitiveValue || '—';
    }

    if (dataType === 'picklist' || dataType === 'multipicklist') {
      return displayValue || primitiveValue || '—';
    }

    if (dataType === 'date') {
      return primitiveValue ? formatDate(primitiveValue) : '—';
    }
    if (dataType === 'datetime') {
      return primitiveValue ? formatDateTime(primitiveValue) : '—';
    }
    if (dataType === 'time') {
      return primitiveValue ? formatTime(primitiveValue) : '—';
    }

    if (dataType === 'boolean' || dataType === 'checkbox') {
      return primitiveValue ? 'Yes' : 'No';
    }

    const formatted = formatFieldValueUtil({
      fieldName,
      label: metadata.field_label || fieldName,
      type: metadata.data_type || dataType,
      value: primitiveValue,
      displayValue: typeof rawValue === 'object' && rawValue !== null ? rawValue.displayValue : null,
      scale: rawValue?.scale ?? metadata.scale,
      precision: rawValue?.precision ?? metadata.precision,
      displayConfig: rawValue?.displayConfig ?? metadata.displayConfig
    });

    if (formatted !== null && formatted !== undefined && formatted !== '') {
      return formatted;
    }

    return primitiveValue !== null && primitiveValue !== undefined && primitiveValue !== ''
      ? String(primitiveValue)
      : '—';
  };

  const handleRowClick = (record) => {
    let recordId = null;
    if (record.id) {
      if (typeof record.id === 'object' && record.id.value) {
        recordId = record.id.value;
      } else if (typeof record.id === 'string') {
        recordId = record.id;
      }
    }

    if (recordId && searchObjectName) {
      navigate(`/view/${searchObjectName}/${recordId}`);
    }
  };

  const getRecordIdValue = (record) => {
    if (!record) return null;
    if (record.id && typeof record.id === 'string') {
      return record.id;
    }
    if (record.id && typeof record.id === 'object' && record.id.value) {
      return record.id.value;
    }
    return record.id || null;
  };

  if (!searchViewId || !searchViewConfig) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view not configured for this widget.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!searchObjectName) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view object not found.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (error && !loading) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  let displayFieldsArray = [];
  if (Array.isArray(searchViewConfig.display_fields)) {
    displayFieldsArray = searchViewConfig.display_fields;
  } else if (typeof searchViewConfig.display_fields === 'string') {
    try {
      displayFieldsArray = JSON.parse(searchViewConfig.display_fields);
    } catch (e) {
      displayFieldsArray = searchViewConfig.display_fields.split(',').map(f => f.trim()).filter(f => f);
    }
  }

  const displayFields = (selectedFields.length > 0
    ? selectedFields
    : displayFieldsArray).filter(fieldName => fieldName !== 'id');

  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#0176d3',
          color: 'rgb(255, 255, 255)'
        }}
      >
        <h5
          className="mb-0"
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.semibold,
            fontSize: theme.typography.fontSize.lg,
            color: 'rgb(255, 255, 255)'
          }}
        >
          {widget?.widgetLabel || 'Search Results'}
        </h5>
        {records.length > 0 && (
          <Button
            variant="link"
            size="sm"
            onClick={handleViewAll}
            style={{ color: 'white', padding: '0 8px', textDecoration: 'none' }}
          >
            View All <FaExternalLinkAlt className="ms-1" />
          </Button>
        )}
      </Card.Header>
      <Card.Body style={{ padding: '1rem', maxHeight: `${maxHeight}px`, overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <p className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>Loading search results...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-3">
            <FaSearch style={{ fontSize: '2rem', color: '#ccc', marginBottom: '0.5rem' }} />
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>No records found</p>
          </div>
        ) : (
          <div className="widget-records" style={{ width: '100%' }}>
            {records.map((record, idx) => {
              const recordId = getRecordIdValue(record);

              return (
                <Card
                  key={recordId || idx}
                  className="mb-2 widget-record-card"
                  style={{
                    border: '1px solid #e0e0e0',
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => handleRowClick(record)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = theme.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }}
                >
                  <Card.Body className="p-3">
                    <Row>
                      {displayFields && displayFields.length > 0 ? (
                        displayFields.map((fieldName, fieldIdx) => {
                          const fieldValue = record[fieldName];

                          const fieldLabel = fieldMetadata[fieldName]?.field_label || fieldName;

                          return (
                            <Col xs={12} md={6} key={fieldIdx} className="mb-2">
                              <div style={{ marginBottom: '4px' }}>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.textSecondary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    marginBottom: '2px'
                                  }}
                                >
                                  {fieldLabel}
                                </div>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.normal,
                                    color: theme.colors.textPrimary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {formatFieldValue(fieldValue, fieldName)}
                                </div>
                              </div>
                            </Col>
                          );
                        })
                      ) : (
                        <Col xs={12}>
                          <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
                            No fields configured for this widget
                          </p>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SearchWidget;
